(() => {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReducedMotion || !("IntersectionObserver" in window)) {
    document.documentElement.classList.add("scroll-reveal-disabled");
    return;
  }

  const revealSelectors = [
    "main > .shopify-section:not(:first-child)",
    ".section-title",
    ".cards__grid > *",
    ".listing__grid > *",
    ".product-carousel",
    ".hero__secondary",
    ".inspiration",
    ".inspiration-block",
    ".shop-the-look-row",
    ".shop-the-look-row > *",
    ".instagram-carousel",
    ".page__header",
    ".page__main > *",
    ".fabrics__group",
    ".showrooms__item",
    ".model-card",
    ".footer__top > *",
    ".footer__bottom > *"
  ];

  const mediaSelectors = [
    ".card",
    ".hero__secondary",
    ".media",
    ".inspiration-block-image-wrapper",
    ".inspiration-block-video-wrapper",
    ".inspiration-item"
  ];

  const excludedSelectors = [
    ".hero",
    ".header",
    ".drawer",
    ".message-window",
    ".page-loader",
    ".swiper-wrapper",
    ".swiper-slide-duplicate",
    "[data-scroll-reveal='false']"
  ];

  const observed = new WeakSet();
  const parentCounts = new WeakMap();
  let pendingRefresh = 0;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.12
    }
  );

  function shouldSkip(element) {
    const isSecondaryHero = element.matches(".hero__secondary");

    if (!element.isConnected) return true;
    if (!isSecondaryHero && element.matches(excludedSelectors.join(","))) return true;
    if (element.closest(".drawer, .message-window, .page-loader, template, [hidden]")) return true;
    return !isSecondaryHero && Boolean(element.closest(".hero"));
  }

  function delayFor(element) {
    const parent = element.parentElement || document.body;
    const current = parentCounts.get(parent) || 0;
    parentCounts.set(parent, current + 1);
    return Math.min(current % 8, 7) * 70;
  }

  function isAlreadyInView(element) {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
  }

  function prepareElement(element) {
    if (observed.has(element) || shouldSkip(element)) return;

    observed.add(element);
    element.classList.add("scroll-reveal");
    element.style.setProperty("--reveal-delay", `${delayFor(element)}ms`);

    if (element.matches(mediaSelectors.join(","))) {
      element.classList.add("scroll-reveal--media");
    }

    if (isAlreadyInView(element)) {
      element.classList.add("is-visible");
      return;
    }

    observer.observe(element);
  }

  function prepare(root = document) {
    root.querySelectorAll(revealSelectors.join(",")).forEach(prepareElement);
  }

  function schedulePrepare(root = document) {
    window.cancelAnimationFrame(pendingRefresh);
    pendingRefresh = window.requestAnimationFrame(() => prepare(root));
  }

  function init() {
    document.documentElement.classList.add("scroll-reveal-ready");
    prepare(document);

    const main = document.querySelector("main");
    if (!main) return;

    new MutationObserver(() => schedulePrepare(main)).observe(main, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => schedulePrepare(event.target));
  document.addEventListener("shopify:section:reorder", () => schedulePrepare(document));
})();
